// features/addressValidation/addressValidationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../utils/apiService";
import { lookupSolanaTokenOnDexScreener } from "@/lib/lookupSolanaTokenOnDexScreener";
import { reportSystemError } from "@/lib/systemErrors/reportSystemError";

const EXPECTED_VALIDATION_MESSAGE_RE =
  /invalid solana address|too long|token not found/i;

function reportTokenValidationError(address, message, details = {}) {
  const text = String(message || "Token validation failed").trim();
  if (!text || EXPECTED_VALIDATION_MESSAGE_RE.test(text)) return;
  reportSystemError({
    phase: "token",
    code: "TOKEN_VALIDATION_FAILED",
    message: text.slice(0, 2000),
    details: {
      tokenAddress: address || null,
      ...details,
    },
  });
}

function reportTokenSaveError(address, error) {
  const text = String(error?.message || error || "Failed to save token").trim();
  if (!text) return;
  reportSystemError({
    phase: "token",
    code: "TOKEN_SAVE_FAILED",
    message: text.slice(0, 2000),
    details: { tokenAddress: address || null },
  });
}

function validationFromDexScreener(dex) {
  return {
    isValid: true,
    tokenName: dex.tokenName,
    tokenSymbol: dex.tokenSymbol,
    tokenMint: dex.tokenMint,
    poolAddress: dex.poolAddress || null,
    resolvedFromPool: Boolean(dex.resolvedFromPool),
    price: dex.price,
    message: dex.resolvedFromPool
      ? "Pool address resolved to token mint"
      : "Token validated successfully",
  };
}

async function saveDexScreenerToken(address, dex) {
  const tokenData = {
    tokenAddress: dex.tokenMint || address,
    tokenName: dex.tokenName,
    tokenSymbol: dex.tokenSymbol,
    price: dex.price || "0.00",
    priceUsd: dex.priceUsd,
    liquidity: dex.liquidity,
    dexId: dex.dexId || "dexscreener",
    pairAddress: dex.poolAddress || null,
    baseToken: dex.tokenMint || null,
    quoteToken: null,
    metadata: {
      dexscreener: true,
      submittedAddress: address,
      validatedAt: new Date().toISOString(),
    },
  };
  try {
    await fetch("/api/tokens/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tokenData),
      credentials: "same-origin",
    });
  } catch (error) {
    console.warn("Failed to save DexScreener token to database:", error);
    reportTokenSaveError(address, error);
  }
}

async function resolveFromBackend(address) {
  try {
    const resolveRes = await apiService.get(
      `/tokens/resolve?address=${encodeURIComponent(address)}`,
      { requiresAuth: false },
    );
    const resolved = resolveRes?.data;
    if (resolved?.success && resolved?.tokenMint) {
      const tokenData = {
        tokenAddress: address,
        tokenName: "Unknown Token",
        tokenSymbol: "UNKNOWN",
        pairAddress: resolved.poolAddress || null,
        metadata: {
          resolvedFromPool: Boolean(resolved.resolvedFromPool),
          submittedAddress: resolved.submittedAddress,
          resolvedAt: new Date().toISOString(),
        },
      };
      try {
        await fetch("/api/tokens/save", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(tokenData),
          credentials: "same-origin",
        });
      } catch (error) {
        console.warn("Failed to save resolved token to database:", error);
        reportTokenSaveError(address, error);
      }

      return {
        isValid: true,
        tokenName: "Unknown Token",
        tokenSymbol: "UNKNOWN",
        tokenMint: resolved.tokenMint,
        poolAddress: resolved.poolAddress || null,
        resolvedFromPool: Boolean(resolved.resolvedFromPool),
        price: null,
        message: resolved.resolvedFromPool
          ? "Pool address resolved to token mint"
          : "Token validated successfully",
      };
    }
  } catch (resolveError) {
    console.warn("Backend token resolve failed:", resolveError);
  }
  return null;
}

/**
 * Thunk: validateAddress
 * @param {string} address  — The wallet address to validate (Solana in this case)
 */

export const validateAddress = createAsyncThunk(
  "addressValidation/validateAddress",
  async (address, { rejectWithValue }) => {
    try {
      // Validate Solana address format and length
      const isValidSolanaFormat = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);

      if (!isValidSolanaFormat) {
        throw new Error("Invalid Solana address format");
      }

      // Check if address is too long (more than 44 characters)
      if (address.length > 44) {
        throw new Error(
          "Address is too long. Solana addresses must be 32-44 characters."
        );
      }

      // First try to validate via our backend API
      let validationResult = null;
      let data = null;
      let jupiterLookupFailed = false;
      let jupiterStatus = null;

      try {
        const res = await fetch(
          `https://lite-api.jup.ag/tokens/v2/search?query=${address}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (res.ok) {
          data = await res.json();
        } else {
          jupiterLookupFailed = true;
          jupiterStatus = res.status;
          console.warn(
            "Jupiter token search failed:",
            `Status ${res.status}: ${await res.text()}`,
          );
        }
      } catch (jupiterErr) {
        jupiterLookupFailed = true;
        console.warn("Jupiter token search failed:", jupiterErr);
      }

      // Jupiter often misses Pump.fun / LaunchLab bonding-curve tokens.
      if (!Array.isArray(data) || data.length === 0) {
        const dex = await lookupSolanaTokenOnDexScreener(address);
        if (dex?.tokenMint) {
          await saveDexScreenerToken(address, dex);
          return validationFromDexScreener(dex);
        }

        const resolved = await resolveFromBackend(address);
        if (resolved) return resolved;

        if (jupiterLookupFailed) {
          reportTokenValidationError(
            address,
            "Token validation failed after Jupiter, DexScreener, and backend lookups",
            { source: "jupiter", status: jupiterStatus },
          );
        }

        return {
          isValid: false,
          tokenName: null,
          tokenSymbol: null,
          price: null,
          message: "Token not found or invalid",
        };
      }

      // If data exists and has token information, the token is valid
      if (data && data.length > 0) {
        const token = data[0];
        validationResult = {
          isValid: true,
          tokenName: token.name || "Unknown Token",
          tokenSymbol: token.symbol || "UNKNOWN",
          tokenMint: token.id || address,
          poolAddress: token.firstPool?.id || null,
          resolvedFromPool: false,
          price: token.usdPrice
            ? parseFloat(token.usdPrice).toFixed(6)
            : null,
          message: "Token validated successfully",
        };

        // Save token to database via our API
        try {
          const tokenData = {
            tokenAddress: address,
            tokenName: token.name || "Unknown Token",
            tokenSymbol: token.symbol || "UNKNOWN",
            price: token.usdPrice
              ? parseFloat(token.usdPrice).toFixed(6)
              : "0.00",
            priceUsd: token.usdPrice ? parseFloat(token.usdPrice) : null,
            marketCap: token.mcap ? parseFloat(token.mcap) : null,
            volume24h: token.stats24h?.buyVolume && token.stats24h?.sellVolume 
              ? parseFloat(token.stats24h.buyVolume) + parseFloat(token.stats24h.sellVolume)
              : null,
            liquidity: token.liquidity ? parseFloat(token.liquidity) : null,
            dexId: "jupiter", // Jupiter API doesn't provide specific DEX info
            pairAddress: token.firstPool?.id || null,
            baseToken: token.id || null,
            quoteToken: null, // Jupiter API doesn't provide quote token info
            metadata: {
              jupiterData: token,
              validatedAt: new Date().toISOString(),
            },
          };

          // Save to database via same-origin BFF (internal key added server-side)
          await fetch("/api/tokens/save", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(tokenData),
            credentials: "same-origin",
          });
        } catch (error) {
          console.warn("Failed to save token to database:", error);
          reportTokenSaveError(address, error);
        }
      } else {
        return {
          isValid: false,
          tokenName: null,
          tokenSymbol: null,
          price: null,
          message: "Token not found or invalid",
        };
      }

      // Return the validation result
      return validationResult;
    } catch (err) {
      const message = err.message || "Token validation failed";
      reportTokenValidationError(address, message);
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  valid: null,
  loading: "idle",
  error: null,
  tokenInfo: null,
};

const addressValidationSlice = createSlice({
  name: "addressValidation",
  initialState,
  reducers: {
    resetValidation(state) {
      state.valid = null;
      state.error = null;
      state.loading = "idle";
      state.tokenInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateAddress.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.valid = null;
        state.tokenInfo = null;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        state.loading = "idle";
        // Add safety check for payload
        if (action.payload && typeof action.payload === 'object') {
          state.valid = action.payload.isValid;
          state.tokenInfo = {
            name: action.payload.tokenName,
            symbol: action.payload.tokenSymbol,
            price: action.payload.price,
            message: action.payload.message,
            mint: action.payload.tokenMint || null,
            poolAddress: action.payload.poolAddress || null,
            resolvedFromPool: Boolean(action.payload.resolvedFromPool),
          };
        } else {
          // Handle case where payload is not an object (shouldn't happen but safety first)
          state.valid = false;
          state.tokenInfo = null;
          state.error = "Invalid response format";
        }
        state.error = null;
      })
      .addCase(validateAddress.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.payload || action.error.message;
        state.valid = false;
        state.tokenInfo = null;
      });
  },
});

export const { resetValidation } = addressValidationSlice.actions;

export default addressValidationSlice.reducer;
